'use client';

import { useState } from 'react';
import { Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * "Version details" disclosure (DR-8): the full sha256, with Copy.
 * A native `<details>` — same disclosure pattern the wireframe uses for
 * this exact element, on every viewport (not only mobile).
 */
export function VersionDetails({ sha256 }: { sha256: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(sha256);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable (permissions/insecure context) — the
      // sha256 is still selectable text, so this is a soft failure.
    }
  }

  return (
    <details className="mt-8 rounded-control border border-border">
      <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-foreground">
        Version details
      </summary>
      <div className="flex flex-wrap items-center gap-3 border-t border-border px-4 py-3">
        <span className="text-xs text-muted-foreground">SHA-256 of the PDF:</span>
        <code className="break-all rounded bg-muted px-2 py-1 font-mono text-xs text-foreground">
          {sha256}
        </code>
        <Button type="button" variant="outline" size="sm" onClick={handleCopy}>
          <Copy aria-hidden="true" />
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <span role="status" aria-live="polite" className="sr-only">
          {copied ? 'SHA-256 copied to clipboard' : ''}
        </span>
      </div>
    </details>
  );
}
